import type { ContactSortDto } from '@carbonwebmail/contracts'
import { useState } from 'react'
import { ColumnPicker } from '../../ui/ColumnPicker.js'
import { Popover } from '../../ui/Popover.js'
import { SortPicker } from '../../ui/SortPicker.js'
import { boardTool } from '../../ui/toolButton.js'
import type { BoardColumn } from './boardColumns.js'

/**
 * What the board shows, and in what order.
 *
 * Two popovers rather than two screens: both answer a question about the table
 * you are looking at, and a person who has to leave it to answer one has lost
 * the thing they were answering it about.
 *
 * **Sorting is the server's.** The board shows a page out of however many there
 * are, so ordering the page would answer a question about the page. The control
 * sets a sort and the rows come back in it.
 *
 * **Hiding is not.** Which columns somebody wants to look at is about this
 * screen and nothing else — no request, and nothing about who the rows are.
 *
 * Both pickers are shared with the segments board. What is left here is the
 * part that is this board's alone: a contact sort names a column *and* a
 * declared field's key, so the id the picker speaks in has to be translated
 * back into one.
 */

export function BoardTools({
  columns,
  hidden,
  sort,
  onHidden,
  onSort,
}: {
  readonly columns: readonly BoardColumn[]
  readonly hidden: ReadonlySet<string>
  readonly sort: ContactSortDto | undefined
  readonly onHidden: (hidden: ReadonlySet<string>) => void
  readonly onSort: (sort: ContactSortDto | undefined) => void
}) {
  const [open, setOpen] = useState<'sort' | 'hide' | undefined>(undefined)
  const sortable = columns.filter((column) => column.sort !== undefined)
  const chosen = sortable.find((column) => same(column.sort, sort))

  return (
    <span className="relative flex items-center gap-1">
      <Popover
        label="Sort"
        onClose={() => setOpen(undefined)}
        onToggle={() => setOpen(open === 'sort' ? undefined : 'sort')}
        align="right"
        open={open === 'sort'}
        panelClassName="w-72 p-3"
        trigger="Sort"
        triggerClassName={boardTool(sort !== undefined)}
      >
        <SortPicker
          chosen={chosen?.id}
          columns={sortable}
          direction={sort?.direction ?? 'asc'}
          onChange={(id, direction) => {
            const column = sortable.find((one) => one.id === id)
            onSort(column?.sort === undefined ? undefined : { ...column.sort, direction })
          }}
          unsorted="Newest first"
        />
      </Popover>

      <Popover
        label="Hide"
        onClose={() => setOpen(undefined)}
        onToggle={() => setOpen(open === 'hide' ? undefined : 'hide')}
        align="right"
        open={open === 'hide'}
        panelClassName="w-72 p-3"
        trigger={hidden.size === 0 ? 'Hide' : `Hide (${hidden.size})`}
        triggerClassName={boardTool(hidden.size > 0)}
      >
        <ColumnPicker columns={columns} hidden={hidden} onHidden={onHidden} />
      </Popover>
    </span>
  )
}

/** A field sort names a key as well as a column, so both have to match. */
function same(a: ContactSortDto | undefined, b: ContactSortDto | undefined): boolean {
  if (a === undefined || b === undefined) return false
  return a.column === b.column && (a.column !== 'field' || a.key === b.key)
}
